import React, { useMemo, useState } from 'react';
import emailjs from '@emailjs/browser';
import { OliveBranch } from './DecorativeElements';
import { Calendar, Mail, Users, Phone, AtSign } from 'lucide-react';

/**
 * EmailJS config (your real values)
 */
const EMAILJS_SERVICE_ID = 'service_udnxwt2';
const EMAILJS_TEMPLATE_ID = 'template_72hewse';
const EMAILJS_PUBLIC_KEY = '8lE8ILbaH1QxsXjFJ';
export function BookingCTA() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState<null | boolean>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Form fields
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  const [childrenAges, setChildrenAges] = useState<string>(''); // "3, 7, 15"
  const [fullName, setFullName] = useState<string>(''); // ✅ to populate {{name}}
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const isEmailValid = email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const isPhoneValid = phone.trim().length > 0 && phone.trim().length >= 6;
  const dateError = useMemo(() => {
    if (!checkIn || !checkOut) return '';
    if (checkOut <= checkIn) return 'Il check-out deve essere dopo il check-in.';
    return '';
  }, [checkIn, checkOut]);
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);
  const canSend = !!checkIn && !!checkOut && !dateError && adults >= 1 && children >= 0 && (children === 0 || childrenAges.trim().length > 0) && fullName.trim().length > 1 && isEmailValid && isPhoneValid && !sending;
  const resetFeedback = () => {
    setSentOk(null);
    setErrorMsg('');
  };
  const resetForm = () => {
    setCheckIn('');
    setCheckOut('');
    setAdults(2);
    setChildren(0);
    setChildrenAges('');
    setFullName('');
    setEmail('');
    setPhone('');
  };
  const sendEmail = async () => {
    resetFeedback();
    if (!canSend) {
      setErrorMsg(dateError || 'Compila tutti i campi richiesti (nome, date, ospiti, età ragazzi se presenti, email e cellulare).');
      return;
    }
    setSending(true);
    try {
      const templateParams = {
        title: 'Richiesta disponibilità WaCa',
        name: fullName.trim() || 'Ospite WaCa',
        checkin: checkIn,
        checkout: checkOut,
        nights: String(nights),
        adults: String(adults),
        children: String(children),
        ages: children > 0 ? childrenAges : '-',
        email: email.trim(),
        phone: phone.trim()
      };
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setSentOk(true);
      // resetForm() // uncomment if you want to clear after success
    } catch (err: any) {
      setSentOk(false);
      setErrorMsg('Errore invio richiesta. Riprova tra poco.');
    } finally {
      setSending(false);
    }
  };
  return <section id="booking" className="py-24 px-4 bg-[var(--sienna)] relative overflow-hidden text-[var(--paper)] scroll-mt-24" data-id="element-89">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-90">
        <OliveBranch className="w-full h-full object-cover text-white transform scale-150 opacity-20" data-id="element-91" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-92">
        <h2 className="font-script text-5xl md:text-7xl mb-6 text-[var(--cream)]" data-id="element-93">
          Prenota il Tuo Soggiorno
        </h2>

        <p className="font-serif text-2xl md:text-3xl mb-8 text-[var(--paper)] opacity-90" data-id="element-94">
          Book Your Stay in Puglia
        </p>

        <p className="text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed opacity-90" data-id="element-95">
          Inserisci le date e i dettagli: invieremo la richiesta direttamente via
          email per disponibilità e miglior tariffa garantita.
        </p>

        {/* Single CTA */}
        <div className="flex justify-center" data-id="element-96">
          <button type="button" onClick={() => {
          resetFeedback();
          setOpen(true);
        }} className="border-2 border-[var(--paper)] text-[var(--paper)] px-10 py-5 rounded-sm font-serif text-xl flex items-center gap-3 hover:bg-[var(--paper)] hover:text-[var(--sienna)] transition-all" data-id="element-97">
            <Calendar className="w-6 h-6" data-id="element-98" />
            <span data-id="element-99">Check Availability</span>
          </button>
        </div>

        <p className="mt-8 text-sm opacity-60 font-serif uppercase tracking-widest" data-id="element-100">
          Minimum stay: 3 nights • Best Price Guaranteed
        </p>

        {/* Modal */}
        {open && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" data-id="element-101">
            {/* Backdrop */}
            <button type="button" className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} aria-label="Chiudi" data-id="element-102" />

            {/* Panel */}
            <div className="relative w-full max-w-2xl bg-[var(--paper)] text-[var(--brown)] border border-[var(--cream)] shadow-2xl" data-id="element-103">
              <div className="p-6 md:p-8 text-left" data-id="element-104">
                <div className="flex items-start justify-between gap-4" data-id="element-105">
                  <div data-id="element-106">
                    <h3 className="font-serif text-2xl md:text-3xl" data-id="element-107">
                      Richiesta disponibilità
                    </h3>
                    <p className="mt-1 text-sm opacity-70 font-serif" data-id="element-108">
                      Compila i dati e clicca <strong data-id="element-109">Invia richiesta</strong>.
                    </p>
                  </div>

                  <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 border border-[var(--cream)] hover:bg-[var(--cream)] transition-colors font-serif" data-id="element-110">
                    Chiudi
                  </button>
                </div>

                {/* Status */}
                {(sentOk === true || sentOk === false || errorMsg) && <div className={`mt-5 p-4 border font-serif text-sm ${sentOk ? 'border-green-300 bg-green-50 text-green-900' : 'border-red-200 bg-red-50 text-red-900'}`} data-id="element-111">
                    {sentOk === true && <div data-id="element-112">✅ Richiesta inviata! Ti risponderemo a breve.</div>}
                    {sentOk === false && <div data-id="element-113">❌ {errorMsg}</div>}
                    {sentOk === null && errorMsg && <div data-id="element-114">⚠️ {errorMsg}</div>}
                  </div>}

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-115">
                  {/* Name */}
                  <div className="md:col-span-2" data-id="element-116">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-117">
                      Nome e Cognome
                    </label>
                    <input type="text" value={fullName} onChange={e => {
                  setFullName(e.target.value);
                  resetFeedback();
                }} placeholder="Nome Cognome" className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-118" />
                  </div>

                  {/* Dates */}
                  <div data-id="element-119">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-120">
                      Check-in
                    </label>
                    <input type="date" value={checkIn} onChange={e => {
                  setCheckIn(e.target.value);
                  resetFeedback();
                }} className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-121" />
                  </div>

                  <div data-id="element-122">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-123">
                      Check-out
                    </label>
                    <input type="date" value={checkOut} onChange={e => {
                  setCheckOut(e.target.value);
                  resetFeedback();
                }} className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-124" />
                  </div>

                  {dateError && <div className="md:col-span-2" data-id="element-125">
                      <p className="text-xs font-serif text-red-700" data-id="element-126">
                        {dateError}
                      </p>
                    </div>}

                  {/* Guests */}
                  <div data-id="element-127">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-128">
                      Adulti
                    </label>
                    <div className="flex items-center gap-3" data-id="element-129">
                      <Users className="w-5 h-5 opacity-60" data-id="element-130" />
                      <input type="number" min={1} value={adults} onChange={e => {
                    setAdults(Math.max(1, Number(e.target.value || 1)));
                    resetFeedback();
                  }} className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-131" />
                    </div>
                  </div>

                  <div data-id="element-132">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-133">
                      Ragazzi (0–17)
                    </label>
                    <div className="flex items-center gap-3" data-id="element-134">
                      <Users className="w-5 h-5 opacity-60" data-id="element-135" />
                      <input type="number" min={0} value={children} onChange={e => {
                    const v = Math.max(0, Number(e.target.value || 0));
                    setChildren(v);
                    if (v === 0) setChildrenAges('');
                    resetFeedback();
                  }} className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-136" />
                    </div>
                  </div>

                  <div className="md:col-span-2" data-id="element-137">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-138">
                      Età ragazzi (0–17)
                      <span className="opacity-60" data-id="element-139">
                        {' '}
                        — es. 3, 7, 15{' '}
                        {children > 0 ? '(obbligatorio)' : '(se presenti)'}
                      </span>
                    </label>
                    <input type="text" value={childrenAges} onChange={e => {
                  setChildrenAges(e.target.value);
                  resetFeedback();
                }} placeholder="Inserisci le età separate da virgole" className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" disabled={children === 0} data-id="element-140" />
                  </div>

                  {/* Contacts */}
                  <div data-id="element-141">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-142">
                      Email di contatto
                    </label>
                    <div className="flex items-center gap-3" data-id="element-143">
                      <AtSign className="w-5 h-5 opacity-60" data-id="element-144" />
                      <input type="email" value={email} onChange={e => {
                    setEmail(e.target.value);
                    resetFeedback();
                  }} placeholder="nome@email.com" className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-145" />
                    </div>
                    {email.trim().length > 0 && !isEmailValid && <p className="mt-2 text-xs text-red-700 font-serif" data-id="element-146">
                        Inserisci un’email valida.
                      </p>}
                  </div>

                  <div data-id="element-147">
                    <label className="block text-sm font-serif mb-2 opacity-80" data-id="element-148">
                      Cellulare di contatto
                    </label>
                    <div className="flex items-center gap-3" data-id="element-149">
                      <Phone className="w-5 h-5 opacity-60" data-id="element-150" />
                      <input type="tel" value={phone} onChange={e => {
                    setPhone(e.target.value);
                    resetFeedback();
                  }} placeholder="+39 ..." className="w-full border border-[var(--cream)] bg-white px-4 py-3 font-serif" data-id="element-151" />
                    </div>
                    {phone.trim().length > 0 && !isPhoneValid && <p className="mt-2 text-xs text-red-700 font-serif" data-id="element-152">
                        Inserisci un numero valido.
                      </p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between" data-id="element-153">
                  <div className="text-sm font-serif opacity-70" data-id="element-154">
                    <span className="inline-flex items-center gap-2" data-id="element-155">
                      <Mail className="w-4 h-4" data-id="element-156" />
                      Invio diretto via email (senza aprire il client).
                    </span>
                  </div>

                  <div className="flex gap-3" data-id="element-157">
                    <button type="button" onClick={resetForm} className="px-6 py-4 border border-[var(--cream)] font-serif hover:bg-[var(--cream)] transition-colors" disabled={sending} data-id="element-158">
                      Reset
                    </button>

                    <button type="button" onClick={sendEmail} disabled={!canSend || !!dateError} className={`inline-flex items-center justify-center gap-3 px-8 py-4 rounded-sm font-serif text-lg border-2 transition-all ${!canSend || !!dateError ? 'bg-transparent text-[var(--brown)] border-[var(--cream)] opacity-60 cursor-not-allowed' : 'bg-[var(--sienna)] text-[var(--paper)] border-[var(--sienna)] hover:opacity-90'}`} data-id="element-159">
                      <Mail className="w-5 h-5" data-id="element-160" />
                      <span data-id="element-161">{sending ? 'Invio...' : 'Invia richiesta'}</span>
                    </button>
                  </div>
                </div>

                <p className="mt-4 text-xs font-serif opacity-60" data-id="element-162">
                  Campi obbligatori: nome, date, adulti, email e cellulare
                  {children > 0 ? ' + età ragazzi' : ''}.
                </p>
              </div>
            </div>
          </div>}
      </div>
    </section>;
}